import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Stack, TextField, PrimaryButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Dropdown, IDropdownOption, Label } from "@fluentui/react";
import { getModifyNGWordData, modifyNGWord } from "../../../api/admin/api_admin";
import { ModifyNGWordResponse } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";

export const ModifyNGWord = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const seq = searchParams.get('seq');
    
    const [data, setData] = useState<ModifyNGWordResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [ngLevel, setNGLevel] = useState<string>("1");
    const [groupCd, setGroupCd] = useState<string>("0");

    useEffect(() => {
        if (seq) {
            fetchData(seq);
        }
    }, [seq]);

    const fetchData = async (seqParam: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getModifyNGWordData(seqParam);
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
                setNGLevel(result.ngword.NGW_ng_level.toString());
                setGroupCd(result.ngword.NGW_group_cd.toString());
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!seq) return;

        setSubmitting(true);
        setError(null);

        try {
            const result = await modifyNGWord({
                seq: seq,
                ng_level: ngLevel,
                group_cd: groupCd
            });

            if (result.success) {
                navigate("/menu/ngwordslist");
            } else {
                setError(result.message || "更新に失敗しました");
            }
        } catch (err) {
            setError("更新中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const getNGLevelOptions = (): IDropdownOption[] => {
        return [
            { key: '1', text: '1 : 警告表示' },
            { key: '2', text: '2 : 生成不可' }
        ];
    };

    const getGroupOptions = (): IDropdownOption[] => {
        if (!data) return [];
        return [
            { key: '0', text: '' },
            ...data.group_list.map(group => ({
                key: group.GRP_group_cd,
                text: `${group.GRP_group_cd} : ${group.GRP_group_name}`
            }))
        ];
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    if (!seq || !data) {
        return (
            <Stack tokens={{ childrenGap: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    {error ?? "SEQが指定されていません"}
                </MessageBar>
                <LinkButton link_to="/menu/ngwordslist" text="戻る" />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="禁止ワード情報変更" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <Stack>
                    <Label>禁止ワード</Label>
                    <div>{data.ngword.NGW_ng_word}</div>
                </Stack>

                <Dropdown
                    label="禁止レベル"
                    options={getNGLevelOptions()}
                    selectedKey={ngLevel}
                    onChange={(_, option) => setNGLevel((option?.key as string) || "1")}
                />

                <Dropdown
                    label="グループCD"
                    options={getGroupOptions()}
                    selectedKey={groupCd}
                    onChange={(_, option) => setGroupCd((option?.key as string) || "0")}
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="登録"
                        onClick={handleSubmit}
                        disabled={submitting}
                    />
                    <LinkButton link_to="/menu/ngwordslist" text="戻る" />
                </Stack>
            </Stack>
        </Stack>
    );
}